import React, {
  useState,
  useCallback,
  memo,
  useEffect,
  useRef
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Router from 'next/router';
import { Formik, Form } from 'formik';

import getMiniQueryParam from '../../utils/getMiniQueryParam';
import useEventListener from '../../utils/hooks/useEventListener';
import trackEvent from '../../utils/tracking';

import Button from '../../atoms/button';
import SearchInput from '../../molecules/global-search';
import IconCross from '../../atoms/icon-cross';
import withSearchActions from '../../organisms/search';
import theme from '../../theme/theme';

import { responsiveCSS } from '../../theme/page';
import manageFocus from '../../utils/manageFocus';
import manageBodyOverflow from '../../utils/manageBodyOverflow';

const SearchWrapper = styled.div`
  align-items: center;
  background: ${(props) => props.theme.color.brand};
  display: flex;
  height: 14.8rem;
  padding: 3rem 0;
  position: fixed;
  top: 0;
  transform: translate3d(0, -100%, 0);
  visibility: ${(props) => (props.isActive ? 'visible' : 'hidden')};
  transition: 0.5s transform,
    visibility ${(props) => props.theme.animation.default};
  width: 100%;
  will-change: transform;
  z-index: ${(props) => props.theme.layers.downstage + 1};

  ${(props) => (props.isActive ? `
    transform: translate3d(0, 0, 0);
  ` : null)}

  ${(props) => (props.isActive && props.canSubmit ? `
    ::after {
      border: 1rem solid transparent;
      border-top-color: ${props.theme.color.brand};
      content: '';
      position: absolute;
      bottom: -2rem;
      left: 25%;
    }
  ` : null)}
`;

const SearchForm = styled(Form)`
  ${responsiveCSS};
  align-items: center;
  display: flex;
  width: 100%;
  position: relative;

  > div {
    padding-right: 1rem;
    width: 100%;

    > button {
      right: 1rem;
    }
  }

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    transition: background-color 5000s ease-in-out 0s;
  }

  input {
    outline: 0;
  }
`;

const Close = styled(Button)`
  height: 3.2rem;
  outline: 0;
  overflow: visible;
  right: 6.5vw;
  top: 1rem;
  width: 3.2rem;

  svg {
    fill: transparent;
    height: 2.8rem;
    stroke-width: 3;
    stroke: #fff;
    width: 2.8rem;
    padding: 0.4rem;
  }
`;

const SearchAction = (props) => {
  const [canSubmit, setCanSubmit] = useState(false);
  const [prevElement, setPrevElement] = useState(null);
  const searchWrapperRef = useRef();

  const submitSearchQuery = (data, { resetForm }) => {
    const pw = props.globalSearchHint;
    const query = {};

    if (props.areSearchResultsCorrected && pw?.length) {
      const [first] = pw;
      query.w = first;
      query.pw = data.w;
    } else {
      query.w = data.w;
    }

    trackEvent({
      eventCategory: 'Search',
      eventAction: 'Full Search Results',
      eventLabel: query.w
    });

    props.updateSearchHint(null);
    props.updateSearchTerm('');
    props.updateSearchResultsCorrected(false);

    resetForm();
    setCanSubmit(false);

    props.toggleGlobalSearchVisibility(false);

    if (props.activeMini !== -1 && props.activeMini) {
      query.af = getMiniQueryParam(props.activeMini, props.petites);
    }

    Router.push({
      pathname: '/shop',
      query
    });
  };

  const handleOnInputChange = useCallback(
    (e) => {
      const input = e.currentTarget;
      const term = input.value;

      setCanSubmit(term.length > 0);

      props.updateSearchTerm(term);
    }, []
  );

  const handleOnCloseClick = () => {
    trackEvent({
      eventCategory: 'Search',
      eventAction: 'Exit Search',
      eventLabel: props.globalSearchTerm
    });
    props.toggleGlobalSearchVisibility(false);
  };

  const handleEscKeypress = ({ code }) => {
    if (code !== 'Escape') return;
    props.toggleGlobalSearchVisibility(false);
  };

  useEventListener('keyup', handleEscKeypress);

  useEffect(() => {
    const { current } = searchWrapperRef;
    const previousElement = global.document.activeElement;
    if (props.isGlobalSearchActive) setPrevElement(previousElement);
    manageFocus(current, props.isGlobalSearchActive, prevElement, 400);

    const isMobile = global.window
      .matchMedia(`(max-width: ${theme.breakpoint.max('small')})`).matches;

    manageBodyOverflow(props.isGlobalSearchActive && isMobile);
  }, [props.isGlobalSearchActive]);

  return (
    <SearchWrapper
      ref={searchWrapperRef}
      id="global-search"
      isActive={props.isGlobalSearchActive}
      canSubmit={canSubmit}
    >
      <Formik
        initialValues={{ w: '', test: '' }}
        onSubmit={submitSearchQuery}
      >
        {() => (
          <SearchForm autoComplete="off">
            <SearchInput
              disabled={!canSubmit}
              hint={props.globalSearchHint}
              onChange={handleOnInputChange}
              isActive={props.isGlobalSearchActive}
              required
            />

            <Close
              aria-label="close search"
              type="button"
              value="close"
              isIcon
              onClick={handleOnCloseClick}
            >
              <IconCross />
            </Close>
          </SearchForm>
        )}
      </Formik>

    </SearchWrapper>
  );
};

SearchAction.defaultProps = {
  activeMini: null,
  globalSearchHint: [],
  areSearchResultsCorrected: false,
  isGlobalSearchActive: false,
  petites: {},
  toggleGlobalSearchVisibility: () => { },
  updateSearchHint: () => { },
  updateSearchTerm: () => { },
  updateSearchResultsCorrected: () => { }
};

SearchAction.propTypes = {
  activeMini: PropTypes.number,
  globalSearchHint: PropTypes.array,
  globalSearchTerm: PropTypes.string.isRequired,
  areSearchResultsCorrected: PropTypes.bool,
  isGlobalSearchActive: PropTypes.bool,
  petites: PropTypes.object,
  toggleGlobalSearchVisibility: PropTypes.func,
  updateSearchHint: PropTypes.func,
  updateSearchTerm: PropTypes.func,
  updateSearchResultsCorrected: PropTypes.func
};

const ConnectedSearchAction = withSearchActions(SearchAction);

ConnectedSearchAction.displayName = 'SearchAction';

export default memo(ConnectedSearchAction);
